import { ComponentProps, ReactNode } from 'react';

import Box, { BoxProps } from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import MuiSwitch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';

import { theme } from '../../theme';

type SwitchSizeVariant = 'regular' | 'small';

const sizeStyles = {
  regular: {
    width: 40,
    height: 24,
    thumbSize: 20,
    checkedTransform: 16,
  },
  small: {
    width: 34,
    height: 20,
    thumbSize: 14,
    checkedTransform: 14,
  },
};

const StyledSwitch = styled(MuiSwitch, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: SwitchSizeVariant }>(({ theme, variant }) => {
  const { width, height, thumbSize, checkedTransform } =
    sizeStyles[variant ?? 'small'];

  return {
    width,
    height,
    padding: 0,
    display: 'flex',

    '& .MuiSwitch-switchBase': {
      padding: (height - thumbSize) / 2,
      '&.Mui-checked': {
        transform: `translateX(${checkedTransform}px)`,
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
        },
        '& .MuiSwitch-thumb': {
          backgroundColor: theme.background?.white,
          boxShadow: theme.colors.shadows.switchThumb,
          width: thumbSize,
          height: thumbSize,
        },
      },
      '&.Mui-disabled': {
        '& + .MuiSwitch-track': {
          opacity: 0.12,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      backgroundColor: theme.background?.white,
      boxShadow: theme.colors.shadows.switchThumb,
      width: thumbSize,
      height: thumbSize,
      borderRadius: thumbSize / 2,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
      border: 'none',
    },

    '& .MuiSwitch-track': {
      borderRadius: height / 2,
      backgroundColor: theme.background?.switchNegative,
      opacity: 1,
      boxSizing: 'border-box',
      border: 'none',
    },
  };
});

type SwitchProps = Omit<ComponentProps<typeof StyledSwitch>, 'size'> & {
  leftLabel?: ReactNode;
  rightLabel?: ReactNode;
  sx?: BoxProps['sx'];
};

export const Switch = ({ leftLabel, rightLabel, sx, ...rest }: SwitchProps) => (
  <Box
    sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: '30px' }}
  >
    {leftLabel && <FormLabel>{leftLabel}</FormLabel>}
    <StyledSwitch {...rest} />
    {rightLabel && <FormLabel>{rightLabel}</FormLabel>}
  </Box>
);
